import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api, { fileUrl } from "@/lib/api";
import { RowSkeleton } from "@/components/Skeletons";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Messages() {
  const nav = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => (await api.get("/conversations")).data.items,
    refetchInterval: 5000,
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="sticky top-0 z-30 glass px-4 pb-3 pt-4">
        <h1 className="font-display text-2xl font-extrabold">Messages</h1>
      </div>

      {isLoading ? (
        <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <div key={i} className="goe-card"><RowSkeleton /></div>)}</div>
      ) : (data || []).length === 0 ? (
        <div className="flex flex-col items-center px-4 py-24 text-center text-dim">
          <MessageCircle size={48} className="mb-4 opacity-40" />
          <p className="font-display text-lg font-bold" style={{ color: "var(--text)" }}>No conversations yet</p>
          <p className="mt-1 text-sm">Message a seller from any listing to start trading</p>
          <button onClick={() => nav("/browse")} className="btn-accent mt-5 px-6 py-2.5 font-bold">Browse market</button>
        </div>
      ) : (
        <div className="space-y-2 p-3">
          {data.map((c, i) => (
            <motion.button key={c.id} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                           whileTap={{ scale: 0.98 }} onClick={() => nav(`/messages/${c.id}`)} data-testid={`conv-${c.id}`}
                           className="goe-card lift flex w-full items-center gap-3 p-3 text-left">
              <div className="relative flex h-13 w-13 items-center justify-center overflow-hidden rounded-full bg-accent text-white"
                   style={{ height: 52, width: 52, minWidth: 52 }}>
                {c.other_user.avatar
                  ? <img src={fileUrl(c.other_user.avatar)} alt="" className="h-full w-full object-cover" />
                  : <span className="text-lg font-bold">{(c.other_user.name?.[0] || "?").toUpperCase()}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-display truncate font-bold">{c.other_user.name}</p>
                  {c.other_user.roles?.[0] && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: "rgba(245,158,11,0.15)", color: "var(--accent)" }}>
                      {c.other_user.roles[0]}
                    </span>
                  )}
                </div>
                <p className="truncate text-sm text-dim">{c.last_message || "Say hello"}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-dim">{timeAgo(c.last_at)}</span>
                {c.unread > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white" style={{ background: "var(--accent)" }}>{c.unread}</span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </motion.div>
  );
}
